import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { TooltipModule } from 'primeng/tooltip';
import { CreateMakeResponse, CreateVehicleModelPayload } from 'src/app/demo/api/vehicles';
import { languages } from 'src/app/demo/constants/languages';
import { LanguageService } from 'src/app/demo/service/language.service';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { VehicleMakesService } from 'src/app/demo/service/vehicle-makes.service';
import { VehicleModelsService } from 'src/app/demo/service/vehicle-models.service';
import { showErrorToast } from 'src/app/demo/utilities/toast-utils';
import { NgxTranslateModule } from 'src/app/translate.module';

@Component({
    selector: 'app-add-vehicle-model',
    standalone: true,
    imports: [
        CommonModule,
        InputTextModule,
        CardModule,
        ButtonModule,
        TooltipModule,
        NgxTranslateModule,
        MessagesModule,
        FormsModule,
        ReactiveFormsModule,
    ],
    templateUrl: './add-vehicle-model.component.html',
    styleUrl: './add-vehicle-model.component.scss',
    providers: [MessageService],
})
export class AddVehicleModelComponent {
    modelsForm!: FormGroup;
    displayRemoveButton: boolean = false;
    makeId!: any;
    make!: CreateMakeResponse;

    languages: any[] = languages;
    selectedLanguage!: string;

    constructor(
        private messageService: MessageService,
        private route: ActivatedRoute,
        private progressLoader: ProgressLoaderService,
        private fb: FormBuilder,
        private languageService: LanguageService,
        private translateService: TranslateService,
        private modelsService: VehicleModelsService,
        private router: Router,
        private makeService: VehicleMakesService
    ) {
        this.initializeForm();
        this.route.queryParamMap.subscribe(params => {
            this.makeId = params.get('makeId');
        });
    }

    ngOnInit() {
        this.fetchVehicleMake();
        this.initializeLocale();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    initializeForm() {
        this.modelsForm = this.fb.group({
            vehicleModels: this.fb.array([]),
        });
        this.addExtraModel();
    }

    get vehicleModelItems(): FormArray {
        return this.modelsForm.get('vehicleModels') as FormArray;
    }

    removeModelBeforeSaving(index: number) {
        this.vehicleModelItems.removeAt(index);
        if (this.vehicleModelItems.length > 1) {
            this.displayRemoveButton = true;
        } else {
            this.displayRemoveButton = false;
        }
    }

    addExtraModel(): void {
        const model = this.fb.group({
            name: [''],
        });
        this.vehicleModelItems.push(model);
        if (this.vehicleModelItems.length > 1) {
            this.displayRemoveButton = true;
        } else {
            this.displayRemoveButton = false;
        }
    }

    extractMakeId() {
        this.route.paramMap.subscribe((params) => {
            this.makeId = params.get('makeId');
        });
    }

    onSubmit(): void {
        this.saveModels();
    }

    backClicked() {
        const state = {
            activePanelIndex: 1,
        }
        this.router.navigate(['/home/vehicle-makes'], { state: state });
    }

    saveModels() {
        this.progressLoader.show();

        const state = {
            activePanelIndex: 1,
        }
        this.modelsService
            .createModel(this.makeId, this.getModelsPayload())
            .subscribe({
                next: (res) => {
                    this.progressLoader.hide();
                    this.translateService.get('MODELS_CREATED_SUCCESS').subscribe(message => {
                        this.showSuccessToast(message);
                    });
                    this.router.navigate(['/home/vehicle-makes'], { state: state });
                },
                error: (err) => {
                    this.progressLoader.hide();
                    showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                },
            });
    }

    fetchVehicleMake() {
        this.progressLoader.show()
        this.makeService.getMakeById(this.makeId).subscribe({
            next: (res) => {
                this.make = res;
                this.progressLoader.hide();
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                this.progressLoader.hide();
            },
        });
    }

    getModelsPayload(): CreateVehicleModelPayload {
        const modelValues = this.modelsForm.value.vehicleModels;
        const models = modelValues.map((model: any) => {
            return model.name;
        });
        return {
            vehicleModels: models,
        };
    }

    showSuccessToast(message: string) {
        this.translateService.get(['Success', message]).subscribe(translations => {
            this.messageService.add({
                severity: 'success',
                summary: translations['Success'],
                life: 2000,
                detail: translations.get(message),
                key: 'tc',
            });
        })
    }

    showErrorToast(error: string) {
        this.translateService.get(['Error', error]).subscribe(translations => {
            this.messageService.add({
                severity: 'error',
                summary: translations['Error'],
                life: 2000,
                detail: translations.get(error),
                key: 'tc',
            });
        })
    }
}
